import {
  addDays,
  localDate,
  type CravingLike,
  type LocalDate,
  type QuitPlan,
  type Trigger,
  type WeekStart,
} from '@tahi/core';
import { useCallback, useMemo } from 'react';
import { logCigarette, removeSmokeLog } from '../../data/commands/quit';
import { useDb } from '../../data/dbContext';
import { useCommandEnv, useData } from '../../data/hooks';
import * as repo from '../../data/repos/quit';
import type { SmokeLogRow } from '../../data/repos/quit';
import { getUserSettings } from '../../data/repos/spending';
import { getState, setState } from '../../data/syncState';
import { useQuery } from '../../data/useQuery';
import { showToast } from '../../ui/toast';
import { useNow } from '../../ui/useNow';
import { copy, errorText } from './copy';
import { logToast } from './format';
import { buildOverview, type QuitOverview } from './overview';

export const DELAY_MS = 10 * 60 * 1000;
const DELAY_KEY = 'quit.delay_until';

/** Today in the user's zone, refreshed every minute so the day rolls over at local midnight. */
export function useQuitToday(): LocalDate {
  const now = useNow(60_000);
  return localDate(now, useData().tz);
}

export interface QuitData {
  plan: QuitPlan | null;
  logs: SmokeLogRow[];
  cravings: CravingLike[];
  weekStart: WeekStart;
}

/** Plan, every live smoke log, and the last 30 days of cravings. Re-reads on any change to these tables. */
export function useQuitData(): QuitData | undefined {
  const { userId } = useData();
  const today = useQuitToday();
  return useQuery(
    ['quit_plans', 'smoke_logs', 'cravings', 'user_settings'],
    async (db) => ({
      plan: await repo.getPlan(db, userId),
      logs: await repo.listSmokeLogs(db, userId),
      cravings: await repo.listCravings(db, userId, { start: addDays(today, -29), end: today }),
      weekStart: ((await getUserSettings(db, userId))?.weekStart ?? 1) as WeekStart,
    }),
    [userId, today],
  ).data;
}

/** undefined while loading; overview is null before a plan exists. */
export function useQuitOverview(): { data: QuitData; overview: QuitOverview | null } | undefined {
  const data = useQuitData();
  const now = useNow(60_000);
  const { tz } = useData();
  return useMemo(() => {
    if (!data) return undefined;
    const overview = data.plan
      ? buildOverview({
          plan: data.plan,
          logs: data.logs,
          cravings: data.cravings,
          now: now.toISOString(),
          tz,
          weekStart: data.weekStart,
        })
      : null;
    return { data, overview };
  }, [data, now, tz]);
}

/** +1 with the plain toast and Undo (used by the dashboard, the tile and the app-icon shortcut). */
export function useLogCigarette(): (trigger?: Trigger | null) => Promise<boolean> {
  const env = useCommandEnv();
  return useCallback(
    async (trigger: Trigger | null = null) => {
      const r = await logCigarette(env, { trigger });
      if (!r.ok) {
        showToast({ message: errorText(r.error) });
        return false;
      }
      showToast({
        message: logToast(r),
        action: {
          label: copy.undo,
          onPress: () => {
            void removeSmokeLog(env, r.id).then((u) => {
              if (u.ok) showToast({ message: copy.removed(u.count) });
            });
          },
        },
      });
      return true;
    },
    [env],
  );
}

/** The "wait 10 minutes" timer, kept in the device-local sync_state so a reload doesn't lose it. */
export function useDelay(): { until: number | null; start(): void; clear(): void } {
  const db = useDb();
  const { data } = useQuery(['sync_state'], (d) => getState(d, DELAY_KEY), []);
  const until = data ? Number(data) : 0;
  const start = useCallback(
    () => void setState(db, DELAY_KEY, String(Date.now() + DELAY_MS)),
    [db],
  );
  const clear = useCallback(() => void setState(db, DELAY_KEY, '0'), [db]);
  return { until: until > 0 ? until : null, start, clear };
}
